import React from 'react';
import { ResultComponentProps } from '../../common/types';
import BaseResultDisplay from '../../common/BaseResultDisplay';
import { Card, Row, Col, Tooltip, Alert, Button, message, Divider, Modal } from 'antd';
import ReactECharts from 'echarts-for-react';
import { CopyOutlined, QuestionCircleOutlined } from '@ant-design/icons';

// 样式定义
const styles = {
  analysisItem: {
    textAlign: 'center' as const,
    padding: '16px',
    backgroundColor: '#fafafa',
    borderRadius: '4px',
  },
  analysisTitle: {
    color: '#666',
    marginBottom: '8px',
  },
  analysisValue: {
    fontSize: '20px',
    fontWeight: 'bold' as const,
    color: '#1890ff',
  },
};

// 定义疏水性数据点类型
interface HydropathyDataPoint {
  position: number;
  hydropathy: number;
  aminoAcid?: string;
}

// 疏水性结果展示组件
const HydropathyResult: React.FC<ResultComponentProps> = ({
  data,
  sequence,
  title,
  onDeleteResult,
  multiResults,
}) => {
  const [showDescription, setShowDescription] = React.useState(false);

  const normalizedResults = React.useMemo(() => {
    if (data && data.results && Array.isArray(data.results)) {
      if (data.results.length === 1 && Array.isArray(data.results[0]?.result?.results)) {
        return data.results[0].result.results;
      }
      return data.results;
    }
    if (Array.isArray(data)) {
      return data;
    }
    return [] as any[];
  }, [data]);

  // 疏水性分析说明内容
  const descriptionContent = (
    <div>
      <p>Kyte & Doolittle疏水性尺度是评估蛋白质疏水性的经典方法，正值表示疏水，负值表示亲水。</p>
      <ul>
        <li>疏水性值 &gt; 0：该区域可能位于蛋白质内部或跨膜区域</li>
        <li>疏水性值 &lt; 0：该区域可能暴露于水溶液环境</li>
        <li>平均疏水性可以预测蛋白质的整体溶解性和稳定性</li>
        <li>窗口大小为9个氨基酸，适合预测跨膜区域和蛋白质折叠模式</li>
        <li>在序列两端使用递减窗口大小计算，确保边界处理的准确性</li>
      </ul>
    </div>
  );

  // 支持多序列显示
  if (normalizedResults.length > 1) {
    const successResults = normalizedResults.filter((item: any) => (item.success !== false) && item.result?.hydropathy_profile);
    
    if (successResults.length === 0) {
      return (
        <BaseResultDisplay
          title={title || '蛋白质疏水性分析'}
          data={data}
          onDeleteResult={onDeleteResult}
          multiResults={multiResults}
          showRawData={true}
          showDistribution={false}
        >
          <Alert
            message="无有效数据"
            description="没有找到有效的疏水性分析结果。"
            type="warning"
            showIcon
          />
        </BaseResultDisplay>
      );
    }

    return (
      <BaseResultDisplay
        title={title || `蛋白质疏水性分析（${successResults.length}个序列）`}
        data={data}
        onDeleteResult={onDeleteResult}
        multiResults={multiResults}
        showRawData={false}
        showDistribution={false}
      >
        {successResults.map((item: any, index: number) => {
          const hydroData: HydropathyDataPoint[] = [];
          const result = item.result || {};
          
          if (Array.isArray(result.hydropathy_profile)) {
            result.hydropathy_profile.forEach((value: any, idx: number) => {
              hydroData.push({
                position: idx + 1,
                hydropathy: Number(value),
              });
            });
          }

          if (hydroData.length === 0) return null;

          // 排序数据点
          hydroData.sort((a, b) => a.position - b.position);
          const currentSequence = item.sequence || result.sequence || '';
          
          // 计算分析指标
          const maxHydro = Math.max(...hydroData.map(point => point.hydropathy));
          const minHydro = Math.min(...hydroData.map(point => point.hydropathy));
          const avgHydro = result.average_hydropathy || (hydroData.reduce((sum, p) => sum + p.hydropathy, 0) / hydroData.length);
          const seqLength = result.sequence_length || currentSequence.length || hydroData.length;

          // 准备图表数据
          const xData = hydroData.map(point => point.position.toString());
          const yData = hydroData.map(point => point.hydropathy);

          // 计算 x 轴标签
          const interval = 20;
          const labelPositions = hydroData.filter(point => ((point.position - 1) % interval === 0) || point.position === seqLength)
                                           .map(point => point.position);
          
          // 构建 rich 文本样式
          const rich: { [key: string]: any } = { idx: { fontSize: 10, lineHeight: 16, color: '#666' } };
          const range = maxHydro - minHydro || 1;
          hydroData.forEach(({ position, hydropathy }) => {
            const norm = (hydropathy - minHydro) / range;
            const start = { r: 24, g: 144, b: 255 };
            const end = { r: 255, g: 77, b: 79 };
            const r = Math.round(start.r + norm * (end.r - start.r));
            const g = Math.round(start.g + norm * (end.g - start.g));
            const b = Math.round(start.b + norm * (end.b - start.b));
            rich[`aa${position}`] = { fontSize: 10, color: `rgb(${r},${g},${b})`, lineHeight: 16 };
          });

          // 图表配置
          const option = {
            tooltip: {
              trigger: 'axis',
              axisPointer: { type: 'shadow' },
              formatter: (params: any) => {
                const idx = params[0].dataIndex;
                const pos = hydroData[idx].position;
                const hydro = hydroData[idx].hydropathy;
                const aa = currentSequence && currentSequence.length >= pos ? currentSequence.charAt(pos - 1) : '';
                return `位置: ${pos}<br/>疏水性: ${hydro.toFixed(2)}<br/>氨基酸: ${aa}`;
              },
            },
            grid: {
              left: '5%',
              right: '5%',
              bottom: '20%',
              top: '10%',
              containLabel: true,
            },
            xAxis: {
              type: 'category',
              data: xData,
              name: '残基位置',
              nameLocation: 'middle',
              nameGap: 50,
              axisTick: { alignWithLabel: true },
              axisLabel: {
                interval: 0,
                margin: 20,
                rich,
                formatter: (value: string, idx: number) => {
                  const pos = Number(value);
                  if (pos < 1 || pos > seqLength) return '';
                  const aa = currentSequence && currentSequence.length >= pos ? currentSequence.charAt(pos - 1) : '';
                  if (labelPositions.includes(pos)) {
                    return `{idx|${pos}}\n{aa${pos}|${aa}}`;
                  } else {
                    return `{idx|${' '.repeat(Math.max(3, pos.toString().length))}}\n{aa${pos}|${aa}}`;
                  }
                },
              },
            },
            yAxis: {
              type: 'value',
              name: '疏水性值',
              nameLocation: 'middle',
              nameGap: 40,
            },
            series: [
              {
                type: 'line',
                data: yData,
                smooth: true,
                lineStyle: { width: 2, color: '#1890ff' },
                symbol: 'circle',
                symbolSize: 6,
                itemStyle: { color: '#1890ff' },
              },
            ],
          };

          return (
            <div key={index}>
              {index > 0 && <Divider />}
              <Card 
                title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>序列 {item.sequence_id || item.id || (index + 1)} - 疏水性分析</span>
                    <Tooltip title="查看分析说明">
                      <QuestionCircleOutlined 
                        style={{ color: '#1890ff', cursor: 'pointer' }}
                        onClick={() => setShowDescription(true)}
                      />
                    </Tooltip>
                  </div>
                }
                style={{ marginBottom: 16 }}
              >
                <ReactECharts
                  option={option}
                  style={{ height: '400px', width: '100%' }}
                  notMerge={true}
                />
                <Card title="疏水性指标分析" bordered={false} style={{ marginTop: 16 }}>
                  <Row gutter={16}>
                    <Col span={6}>
                      <div style={styles.analysisItem}>
                        <div style={styles.analysisTitle}>平均疏水性</div>
                        <div style={styles.analysisValue}>{avgHydro.toFixed(2)}</div>
                      </div>
                    </Col>
                    <Col span={6}>
                      <div style={styles.analysisItem}>
                        <div style={styles.analysisTitle}>最大疏水性</div>
                        <div style={styles.analysisValue}>{maxHydro.toFixed(2)}</div>
                      </div>
                    </Col>
                    <Col span={6}>
                      <div style={styles.analysisItem}>
                        <div style={styles.analysisTitle}>最小疏水性</div>
                        <div style={styles.analysisValue}>{minHydro.toFixed(2)}</div>
                      </div>
                    </Col>
                    <Col span={6}>
                      <div style={styles.analysisItem}>
                        <div style={styles.analysisTitle}>序列长度</div>
                        <div style={styles.analysisValue}>{seqLength}</div>
                      </div>
                    </Col>
                  </Row>
                </Card>
              </Card>
            </div>
          );
        })}
        
        {/* 说明弹窗 */}
        <Modal
          title="疏水性分析说明"
          open={showDescription}
          onCancel={() => setShowDescription(false)}
          footer={null}
          width={600}
        >
          <Alert
            type="info"
            showIcon
            message="Kyte & Doolittle疏水性尺度"
            description={descriptionContent}
          />
        </Modal>
      </BaseResultDisplay>
    );
  }

  // 单序列模式的原有逻辑保持不变
  const [selectedPoint, setSelectedPoint] = React.useState<HydropathyDataPoint | null>(null);

  // 处理疏水性数据
  const hydroData: HydropathyDataPoint[] = [];

  if (data && Array.isArray((data as any).hydropathy_profile)) {
    // 直接数据格式
    (data as any).hydropathy_profile.forEach((value: any, index: number) => {
      hydroData.push({
        position: index + 1,
        hydropathy: Number(value),
      });
    });
  } else if (data && data.results && Array.isArray(data.results)) {
    // 统一任务API返回的包装格式：{ results: [{ result: { hydropathy_profile: [...] } }] }
    const sourceResults = data.results.length === 1 && Array.isArray(data.results[0]?.result?.results)
      ? data.results[0].result.results
      : data.results;
    if (sourceResults.length === 1 && sourceResults[0]?.result?.hydropathy_profile) {
      sourceResults[0].result.hydropathy_profile.forEach((value: any, index: number) => {
        hydroData.push({
          position: index + 1,
          hydropathy: Number(value),
        });
      });
    }
  }

  // 如果没有有效数据，显示错误信息
  if (hydroData.length === 0) {
    console.error('无法解析疏水性数据:', data);
    return (
      <BaseResultDisplay
        title={title || '蛋白质疏水性分析'}
        data={data}
        onDeleteResult={onDeleteResult}
        multiResults={multiResults}
        showRawData={true}
        showDistribution={false}
      >
        <Alert
          message="数据格式错误"
          description="无法解析疏水性数据，请检查数据格式。"
          type="error"
          showIcon
        />
      </BaseResultDisplay>
    );
  }

  // 排序数据点（按残基位置从小到大）
  hydroData.sort((a, b) => a.position - b.position);

  // 准备图表数据：xData 为位置字符串，yData 为疏水性值
  const xData = hydroData.map(item => item.position.toString());
  const yData = hydroData.map(item => item.hydropathy);

  // 计算分析指标
  const maxHydro = Math.max(...hydroData.map(item => item.hydropathy));
  const minHydro = Math.min(...hydroData.map(item => item.hydropathy));

  // 从统一任务API格式中提取平均疏水性和序列长度
  let avgHydro = (data as any).average_hydropathy || 0;
  let seqLength = (data as any).sequence_length || hydroData.length;
  if (data && data.results && Array.isArray(data.results)) {
    const sourceResults = data.results.length === 1 && Array.isArray(data.results[0]?.result?.results)
      ? data.results[0].result.results
      : data.results;
    if (sourceResults.length === 1 && sourceResults[0]?.result) {
      avgHydro = sourceResults[0].result.average_hydropathy || avgHydro;
      seqLength = sourceResults[0].result.sequence_length || seqLength;
    }
  }

  // 计算 x 轴标签：仅每 interval 个残基显示序号，所有残基显示氨基酸
  const interval = 20;
  const labelPositions = React.useMemo(
    () => hydroData.filter(item => ((item.position - 1) % interval === 0) || item.position === seqLength)
                     .map(item => item.position),
    [hydroData, seqLength]
  );
  // 构建 rich 文本样式，动态为所有残基定义氨基酸颜色，编号样式
  const rich: { [key: string]: any } = { idx: { fontSize: 10, lineHeight: 16, color: '#666' } };
  const range = maxHydro - minHydro || 1;
  hydroData.forEach(({ position, hydropathy }) => {
    // 计算渐变色，亲水(最小)为蓝，疏水(最大)为红
    const norm = (hydropathy - minHydro) / range;
    const start = { r: 24, g: 144, b: 255 };
    const end = { r: 255, g: 77, b: 79 };
    const r = Math.round(start.r + norm * (end.r - start.r));
    const g = Math.round(start.g + norm * (end.g - start.g));
    const b = Math.round(start.b + norm * (end.b - start.b));
    rich[`aa${position}`] = { fontSize: 10, color: `rgb(${r},${g},${b})`, lineHeight: 16 };
  });

  // 图表配置
  const option = {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params: any) => {
        const idx = params[0].dataIndex;
        const pos = hydroData[idx].position;
        const hydro = hydroData[idx].hydropathy;
        const aa = sequence && sequence.length >= pos ? sequence.charAt(pos - 1) : '';
        return `位置: ${pos}<br/>疏水性: ${hydro.toFixed(2)}<br/>氨基酸: ${aa}`;
      },
    },
    grid: {
      left: '5%',
      right: '5%',
      bottom: '20%',  // 进一步增加底部空间
      top: '10%',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: xData,
      name: '残基位置',
      nameLocation: 'middle',
      nameGap: 50,
      axisTick: { alignWithLabel: true },
      axisLabel: {
        interval: 0,
        margin: 20,
        rich,
        formatter: (value: string, idx: number) => {
          const pos = Number(value);
          if (pos < 1 || pos > seqLength) return '';
          const aa = sequence && sequence.length >= pos ? sequence.charAt(pos - 1) : '';
          if (labelPositions.includes(pos)) {
            return `{idx|${pos}}\n{aa${pos}|${aa}}`;
          } else {
            // 非编号位置只显示氨基酸，前置空行，添加空格确保对齐
            return `{idx|${' '.repeat(Math.max(3, pos.toString().length))}}\n{aa${pos}|${aa}}`;
          }
        },
      },
    },
    yAxis: {
      type: 'value',
      name: '疏水性值',
      nameLocation: 'middle',
      nameGap: 40,
    },
    series: [
      {
        type: 'line',
        data: yData,
        smooth: true,
        lineStyle: { width: 2, color: '#1890ff' },
        symbol: 'circle',
        symbolSize: 6,
        itemStyle: { color: '#1890ff' },
        markLine: {
          silent: true,
          lineStyle: { color: '#999', type: 'dashed' },
          // data: [{ yAxis: 0, label: { formatter: '', position: 'start' } }],
        },
      },
    ],
  };

  // 处理图表点击事件
  const onChartClick = (params: any) => {
    const position = params.data[0];
    const hydropathy = params.data[1];
    const aa = sequence && sequence.length >= position ? sequence.charAt(position - 1) : '';
    setSelectedPoint({ position, hydropathy, aminoAcid: aa });
  };

  const onEvents = {
    click: onChartClick,
  };

  return (
    <BaseResultDisplay
      title={title || '蛋白质疏水性分析'}
      data={data}
      onDeleteResult={onDeleteResult}
      multiResults={multiResults}
      showRawData={false}
      showDistribution={false}
    >
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card 
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>蛋白质疏水性分析 (Kyte & Doolittle)</span>
                <Tooltip title="查看分析说明">
                  <QuestionCircleOutlined 
                    style={{ color: '#1890ff', cursor: 'pointer' }}
                    onClick={() => setShowDescription(true)}
                  />
                </Tooltip>
              </div>
            }
            bordered={false}
          >
            <ReactECharts
              option={option}
              style={{ height: '400px', width: '100%' }}
              onEvents={onEvents}
            />
            {selectedPoint && (
              <div style={{ marginTop: 16, textAlign: 'center' }}>
                <Tooltip title="当前选中的点">
                  <span
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#f0f0f0',
                      borderRadius: '4px',
                      display: 'inline-block',
                    }}
                  >
                    位置: <b>{selectedPoint.position}</b>, 疏水性: <b>{selectedPoint.hydropathy.toFixed(2)}</b>, 氨基酸: <b>{selectedPoint.aminoAcid}</b>
                  </span>
                </Tooltip>
                <Button
                  size="small"
                  icon={<CopyOutlined />}
                  style={{ marginLeft: 8 }}
                  onClick={() => {
                    if (selectedPoint.aminoAcid) {
                      navigator.clipboard.writeText(selectedPoint.aminoAcid);
                      message.success('氨基酸已复制');
                    }
                  }}
                />
              </div>
            )}
          </Card>
        </Col>
        <Col span={24}>
          <Card title="疏水性指标分析" bordered={false}>
            <Row gutter={16}>
              <Col span={6}>
                <div style={styles.analysisItem}>
                  <div style={styles.analysisTitle}>平均疏水性</div>
                  <div style={styles.analysisValue}>{avgHydro.toFixed(2)}</div>
                </div>
              </Col>
              <Col span={6}>
                <div style={styles.analysisItem}>
                  <div style={styles.analysisTitle}>最大疏水性</div>
                  <div style={styles.analysisValue}>{maxHydro.toFixed(2)}</div>
                </div>
              </Col>
              <Col span={6}>
                <div style={styles.analysisItem}>
                  <div style={styles.analysisTitle}>最小疏水性</div>
                  <div style={styles.analysisValue}>{minHydro.toFixed(2)}</div>
                </div>
              </Col>
              <Col span={6}>
                <div style={styles.analysisItem}>
                  <div style={styles.analysisTitle}>序列长度</div>
                  <div style={styles.analysisValue}>{seqLength}</div>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
      
      {/* 说明弹窗 */}
      <Modal
        title="疏水性分析说明"
        open={showDescription}
        onCancel={() => setShowDescription(false)}
        footer={null}
        width={600}
      >
        <Alert
          type="info"
          showIcon
          message="Kyte & Doolittle疏水性尺度"
          description={descriptionContent}
        />
      </Modal>
    </BaseResultDisplay>
  );
};

export default HydropathyResult;